import { addWeeks } from "date-fns"
import { z } from "zod"

import type { PreorderBook } from "~/utils/demandInsightValues"
import { PLAN_PERIOD_WEEKS } from "~/utils/demandPlanPeriod"

/**
 * How the movement in the known order book (`knownNow` vs `knownAYearAgo`) is carried into the full-year
 * projection:
 * - `closeBook` — nothing more arrives, so the projection is today's book
 * - `maintain` — ignore the movement, so the year lands on last year
 * - `adaptChange` — carry the absolute unit movement onto last year
 * - `adaptTrend` — carry the proportional movement onto last year
 */
export type AdaptationMode = "closeBook" | "maintain" | "adaptChange" | "adaptTrend"

export const adaptationSchema = z.enum(["closeBook", "maintain", "adaptChange", "adaptTrend"])

/** Exported: the y-axis ceiling is sized against every answer, not just the chosen one. */
export const projectionFor = (
  mode: AdaptationMode,
  lastYear: number,
  knownNow: number,
  diff: number,
  diffPct: number | null,
): number => {
  switch (mode) {
    case "closeBook":
      return knownNow
    case "maintain":
      return lastYear
    case "adaptChange":
      return Math.max(lastYear + diff, 0)
    case "adaptTrend":
      return diffPct == null ? lastYear : Math.max(lastYear * (1 + diffPct), 0)
  }
}

export const clampKnownShare = (share: number): number => Math.min(Math.max(share, 0), 1)

/**
 * The pre-order profile's weekly known shares moved so their baseline-weighted mean lands on the
 * planner's share instead of the profile's `measured` one: below it each week's known part shrinks
 * by one factor, above it each week's unknown part does, so every week stays within 0–1.
 */
export const shiftKnownShares = (
  shares: readonly number[],
  measured: number,
  target: number,
): number[] => {
  const to = clampKnownShare(target)
  if (to < measured) return shares.map((k) => (k * to) / measured)
  if (measured >= 1) return [...shares]
  return shares.map((k) => 1 - ((1 - k) * (1 - to)) / (1 - measured))
}

type NoosBookTotals = { lastYear: number; knownAYearAgo: number; knownNow: number }

/**
 * The book a year ago under the planner's "share known by now" (`preorderKnownShare`, already
 * clamped), which replaces the pre-order profile's share of `lastYear`, never the pre-orders.
 */
const withKnownShare = <B extends NoosBookTotals>(book: B, share: number | null): B =>
  share == null ? book : { ...book, knownAYearAgo: book.lastYear * share }

const unitsOn = (measured: NoosBookTotals, mode: AdaptationMode, share: number | null): number => {
  const book = withKnownShare(measured, share)
  const diff = book.knownNow - book.knownAYearAgo
  const diffPct = book.knownAYearAgo > 0 ? diff / book.knownAYearAgo : null
  return projectionFor(mode, book.lastYear, book.knownNow, diff, diffPct) - book.lastYear
}

/**
 * A NOOS variant's "Pre-order development" units: its projected year of pre-orders minus the realised
 * pre-orders already in its baseline (`lastYear`), so the insight replaces them rather than adding on
 * top. The projection is `projectionFor` over its own book, as the card's chart draws it.
 */
export const noosPreorderUnitsFor = (
  measured: NoosBookTotals,
  mode: AdaptationMode,
  overrideShare: number | null = null,
): number => unitsOn(measured, mode, overrideShare == null ? null : clampKnownShare(overrideShare))

type PreorderWeek = {
  ms: number
  lastYear: number
  knownAYearAgo: number
  knownNow: number
}

export type PreorderDevelopmentFigures = {
  weeks: PreorderWeek[]
  totals: { lastYear: number; knownAYearAgo: number; knownNow: number }
  /** The like-for-like movement the whole question is about: `knownNow - knownAYearAgo`. */
  diff: number
  diffPct: number | null
  projected: number
  /** Still expected under the answer. Signed: below the current book means no further intake. */
  development: number
  /**
   * The row's headline, not `development`: on the same "vs LY" axis as the breakdown and
   * `salesTrend`.
   */
  vsLastYear: number
  vsLastYearPct: number | null
}

const NO_TOTALS = { lastYear: 0, knownAYearAgo: 0, knownNow: 0 }

/** The pre-order profile's weekly known share (`PreorderFactors`), by week start. */
export type KnownShareProfile = readonly { weekStart: string; knownShare: number }[]

type NoosPreorderWeek = PreorderWeek & {
  /** The profile's known share, moved onto the planner's share; null without a profile row. */
  knownShare: number | null
}

/** The NOOS breakdown's figures: the summed book plus every answer's projection. */
export type NoosPreorderFigures = Omit<PreorderDevelopmentFigures, "weeks"> & {
  weeks: NoosPreorderWeek[]
  projectedByMode: Record<AdaptationMode, number>
  /**
   * `projectedByMode` on the measured share, ignoring the override: sizes the pinned axis, which
   * must not move when the planner drags the share.
   */
  measuredProjectedByMode: Record<AdaptationMode, number>
  /** The override if set, else `measuredKnownShare`; `totals.knownAYearAgo` is built from it. */
  knownShare: number
  /** The pre-order profile's share over the remaining weeks, baseline-weighted; kept under an override. */
  measuredKnownShare: number
  overrideShare: number | null
  /**
   * The weeks in the plan the books cover: every variant's count when they share one, the whole
   * period when any covers it, else null (they cover different parts of it).
   */
  weeksInPlan: number | null
}

const ADAPTATION_MODES: readonly AdaptationMode[] = adaptationSchema.options

const weeksInPlanOf = (books: readonly PreorderBook[]): number | null => {
  const counts = new Set(books.map((b) => b.weeksInPlan ?? PLAN_PERIOD_WEEKS))
  if (counts.size === 0 || counts.has(PLAN_PERIOD_WEEKS)) return PLAN_PERIOD_WEEKS
  return counts.size === 1 ? [...counts][0]! : null
}

/**
 * The NOOS breakdown's figures off the engine's books (`InsightScalars.preorderBook`), never a read of
 * its own, so the chart draws exactly what the insight is sized on. Each answer's projection is the
 * sum of every variant's own (`noosPreorderUnitsFor`), as the engine applies it, not one projection
 * of the summed book.
 */
export const computeNoosPreorderDevelopment = (
  books: readonly PreorderBook[],
  mode: AdaptationMode,
  currentWeek: Date,
  /** The planner's "share known by now", 0–1; replaces the profile's share, not the pre-orders. */
  overrideShare: number | null = null,
  profile: KnownShareProfile = [],
): NoosPreorderFigures => {
  const override = overrideShare == null ? null : clampKnownShare(overrideShare)
  const measured = books.reduce(
    (acc, b) => ({
      lastYear: acc.lastYear + b.lastYear,
      knownAYearAgo: acc.knownAYearAgo + b.knownAYearAgo,
      knownNow: acc.knownNow + b.knownNow,
    }),
    NO_TOTALS,
  )
  const measuredKnownShare = measured.lastYear > 0 ? measured.knownAYearAgo / measured.lastYear : 0
  const totals = withKnownShare(measured, override)
  const byMode = (share: number | null) =>
    Object.fromEntries(
      ADAPTATION_MODES.map((m) => [
        m,
        books.reduce((sum, b) => sum + b.lastYear + unitsOn(b, m, share), 0),
      ]),
    ) as Record<AdaptationMode, number>
  const projectedByMode = byMode(override)
  const measuredProjectedByMode = override == null ? projectedByMode : byMode(null)
  const weekCount = Math.max(0, ...books.map((b) => b.weeks.lastYear.length))
  const weekMs = [...Array(weekCount).keys()].map((i) => addWeeks(currentWeek, i).getTime())
  const profileByMs = new Map(profile.map((p) => [Date.parse(p.weekStart), p.knownShare]))
  const profileShares = weekMs.map((ms) => profileByMs.get(ms) ?? null)
  // The same measured share the totals use, so the line's baseline-weighted mean lands on `override`.
  const shares =
    override == null
      ? profileShares
      : shiftKnownShares(
          profileShares.map((k) => k ?? 0),
          measuredKnownShare,
          override,
        ).map((k, i) => (profileShares[i] == null ? null : k))
  const weeks = weekMs.map((ms, i) => ({
    ms,
    lastYear: books.reduce((sum, b) => sum + (b.weeks.lastYear[i] ?? 0), 0),
    knownAYearAgo: books.reduce((sum, b) => sum + (b.weeks.knownAYearAgo[i] ?? 0), 0),
    knownNow: books.reduce((sum, b) => sum + (b.weeks.knownNow[i] ?? 0), 0),
    knownShare: shares[i] ?? null,
  }))
  const diff = totals.knownNow - totals.knownAYearAgo
  const projected = projectedByMode[mode]
  return {
    weeks,
    totals,
    diff,
    diffPct: totals.knownAYearAgo > 0 ? diff / totals.knownAYearAgo : null,
    projected,
    projectedByMode,
    measuredProjectedByMode,
    knownShare: override ?? measuredKnownShare,
    measuredKnownShare,
    overrideShare: override,
    weeksInPlan: weeksInPlanOf(books),
    development: projected - totals.knownNow,
    vsLastYear: projected - totals.lastYear,
    vsLastYearPct: totals.lastYear > 0 ? (projected - totals.lastYear) / totals.lastYear : null,
  }
}
